import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {Pin} from 'lucide-react';
import api, { chatSessionAPI, spacesAPI, customAgentAPI } from '../services/api.js';
import { FolderPlus, Wrench, Paperclip, Send } from 'lucide-react';
import { Menu, X } from 'lucide-react';
import ActiveAgentDock from '../components/ActiveAgentDock.jsx';
import ContextPanel from '../components/ContextPanel.jsx';
import ActiveParticipantsDock from '../components/ActiveParticipantsDock.jsx';
import AgentPicker from '../components/AgentPicker.jsx';
import { useComposerPicks, ToolsPicker, ModelPicker } from '../components/agentsWorkflows/ComposerPickers.jsx';
import toast from 'react-hot-toast';
import { getAuthToken, getUserIdFromToken } from '../utils/auth.js';
import { useAI } from '../context/AIContext';
import { alpha } from '../utils/alpha';
import ChatBubble from '../components/chat/ChatBubble.jsx';
import { AgentEventRow, DirectoryCard, RequestCard, SponsorCard } from '../components/spaces/TeamAgentCards.jsx';
import ShareAgentMenu from '../components/spaces/ShareAgentMenu.jsx';
import useAgentSession, { typedAgentIn } from '../components/spaces/useAgentSession.js';
import { AgentSessionPill, TeamAgentStrip } from '../components/spaces/AgentSessionBar.jsx';
import { useRoster } from '../components/spaces/TeamAgentCards.jsx';

// ─── Theme ────────────────────────────────────────────────────────────────────
const T = {
  green:       '#2F6E5B',
  greenLight:  'var(--app-surface-3)',
  greenMid:    '#4A9B82',
  border:      'var(--lyra-color-border)',
  borderLight: 'var(--app-surface-3)',
  bg:          'var(--lyra-color-surface-2)',
  bgWhite:     'var(--lyra-color-surface)',
  text:        'var(--lyra-color-text)',
  textMid:     'var(--app-text-2)',
  textSoft:    'var(--lyra-color-text-muted)',
  textMuted:   'var(--app-text-3)',
  accent:      'var(--lyra-color-primary)',
  red:         'var(--lyra-color-danger)',
};

// Design tokens matching the redesigned app shell (RunDrawer / SpaceDetailsPage /
// AgentDetailsPage etc.) — used by ComposeBar and SpaceFeed so the Space
// "Conversations" tab and the New Task / RunDrawer composer look and behave
// the same, per the "keep it in sync" feedback. The rest of this file (the
// standalone /chats hub, which is no longer reachable from the redesigned
// nav) keeps the older green `T` palette above.
const T2 = {
  page:        'var(--lyra-color-surface)',
  surface:     'var(--lyra-color-surface)',
  surface2:    'var(--lyra-color-surface-2)',
  border:      'var(--lyra-color-border)',
  border2:     'var(--app-border-strong)',
  ink:         'var(--lyra-color-text)',
  ink2:        'var(--app-text-2)',
  ink3:        'var(--lyra-color-text-muted)',
  ink4:        'var(--app-text-3)',
  accent:      'var(--lyra-color-primary)',
  accentLight: 'var(--app-primary-soft)',
  accentMid:   'var(--app-primary-soft-2)',
  accentDark:  'var(--app-primary-deep)',
  green:       'var(--lyra-color-success)',
  greenBg:     'var(--lyra-color-success-bg)',
  violet:      'var(--lyra-color-primary)',
  violetBg:    'var(--app-primary-soft)',
  violetBd:    'var(--app-primary-soft-2)',
  red:         'var(--lyra-color-danger)',
};

const css = {
  sidebarWidth: 292,
  ctxWidth: 340,
};
const EMPTY_MENTION_OPTIONS = [];

// Safe icon fallbacks if `lucide-react` does not export a symbol
const WrenchIcon = typeof Wrench === 'function' ? Wrench : ({ size = 14 }) => <span style={{ fontSize: size }}>🔧</span>;
const PaperclipIcon = typeof Paperclip === 'function' ? Paperclip : ({ size = 16 }) => <span style={{ fontSize: size }}>📎</span>;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const timeAgo = (date) => {
  if (!date) return '';
  const ms = Date.now() - new Date(date).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const renderMarkdown = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code style="background:var(--app-surface-3);padding:1px 5px;border-radius:4px;font-family:monospace;font-size:12px">$1</code>')
    .replace(/\n/g, '<br>');
};

const normalizeIdentity = (value) => String(value || '').trim().toLowerCase();

function MessageAttachments({ attachments = [], mine = false }) {
  if (!Array.isArray(attachments) || attachments.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', gap: 6, marginBottom: 6, maxWidth: '80%' }}>
      {attachments.map((file, index) => {
        const name = file?.filename || file?.originalName || file?.name || 'Attachment';
        const body = (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
            <PaperclipIcon size={14} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          </span>
        );
        const style = {
          maxWidth: '100%', padding: '8px 11px', borderRadius: 10,
          border: `1px solid ${mine ? 'var(--app-primary-soft-2)' : T2.border}`,
          background: mine ? 'var(--app-primary-soft)' : T2.surface,
          color: mine ? T2.accentDark : T2.ink2,
          fontSize: 12, fontWeight: 600, textDecoration: 'none',
        };
        return file?.url
          ? <a key={file.fileId || file.id || index} href={file.url} target="_blank" rel="noreferrer" style={style}>{body}</a>
          : <div key={file?.fileId || file?.id || index} style={style}>{body}</div>;
      })}
    </div>
  );
}

// ─── Shared Toolbar (bottom of both feeds) ───────────────────────────────────
function ComposeBar({
  value, onChange, onSend, sending, placeholder,
  attachedAgents, onAddAgent, onRemoveAgent, onOpenAgent,
  onMentionAgent,
  mentionOptions,
  showMentions = true,
  onAttach,
  attachedFilesCount = 0,
  picks = null,        // useComposerPicks() — when given, Tools is the real skills picker
  modelId = null,      // 'auto' | model id — when onModelChange is given, a model picker is shown
  onModelChange = null,
  extraTools = null,   // more buttons for the toolbar (a team conversation adds Share)
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [mentionAnchor, setMentionAnchor] = useState(null); // { top, left }
  const [agentWorkflowList, setAgentWorkflowList] = useState([]);
  const inputRef = useRef(null);
  const barRef = useRef(null);
  const safeMentionOptions = Array.isArray(mentionOptions) ? mentionOptions : EMPTY_MENTION_OPTIONS;

  // auto-resize textarea up to 5 lines
  const adjustHeight = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    const max = 5 * 24; // approx 5 lines (24px line-height)
    const h = Math.min(el.scrollHeight, max);
    el.style.height = h + 'px';
  }, []);

  // Detect @mention as user types
  useEffect(() => {
    const at = value.lastIndexOf('@');
    if (at === -1) { setSuggestions([]); return; }
    // Only trigger if @ is at start or preceded by space
    const before = value[at - 1];
    if (before && before !== ' ') { setSuggestions([]); return; }
    const q = value.slice(at + 1);
    // stop if space after @ (mention closed)
    if (q.includes(' ') && q.trim().length > 0 && !attachedAgents.some(a => q.trim().startsWith((a.name||'').toLowerCase()))) {
      setSuggestions([]); return;
    }
    // Ensure we have a combined agents + workflows list (cached)
    (async () => {
      try {
        if (!agentWorkflowList || agentWorkflowList.length === 0) {
          const [agentsRes, chainsRes] = await Promise.allSettled([customAgentAPI.list(), customAgentAPI.listChains()]);
          const agents = (agentsRes.status === 'fulfilled' ? (agentsRes.value?.agents || agentsRes.value || []) : []);
          const chains = (chainsRes.status === 'fulfilled' ? (chainsRes.value?.chains || chainsRes.value || []) : []);
          const merged = [
            ...agents.map(a => ({ _id: a._id || a.id, name: a.name, description: a.description, _type: 'agent' })),
            ...chains.map(c => ({ _id: c._id || c.id, name: c.name, description: c.description, _type: 'workflow' })),
          ];
          setAgentWorkflowList(merged);
        }
      } catch (e) { /* ignore */ }

      const baseSource = (agentWorkflowList && agentWorkflowList.length > 0) ? agentWorkflowList : [];
      const source = [
        ...safeMentionOptions,
        ...baseSource.filter(item => !safeMentionOptions.some(opt => String(opt._id || opt.id) === String(item._id || item.id))),
      ];
      const filtered = source.filter(item => (item.name || '').toLowerCase().startsWith(q.toLowerCase())).slice(0, 3);
      setSuggestions(filtered);
    })();
    // position relative to textarea caret — simplified: anchor to top of bar
    if (inputRef.current && barRef.current) {
      const barRect = barRef.current.getBoundingClientRect();
      setMentionAnchor({ bottom: barRef.current.offsetHeight + 8 });
    }
  }, [value, attachedAgents, agentWorkflowList, safeMentionOptions]);

  const pickSuggestion = (agent) => {
    const at = value.lastIndexOf('@');
    const before = value.slice(0, at);
    // workflow mention uses special format @workflow:name
    const nextMsg = agent._type === 'workflow'
      ? `${before}@workflow:${agent.name} `
      : `${before}@${agent.name} `;
    onChange(nextMsg);
    onMentionAgent?.(agent);
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const handleKey = (e) => {
    if (e.key === 'Escape') { setSuggestions([]); return; }
    if (e.key === 'Enter' && !e.shiftKey) {
      if (suggestions.length > 0) { pickSuggestion(suggestions[0]); return; }
      e.preventDefault(); onSend();
    }
  };

  const [focused, setFocused] = useState(false);
  // A card or chip that starts a conversation asks for the box, so the person can simply type.
  useEffect(() => {
    const focusBox = () => setTimeout(() => inputRef.current?.focus(), 0);
    window.addEventListener('lyra:focus-composer', focusBox);
    return () => window.removeEventListener('lyra:focus-composer', focusBox);
  }, []);

  return (
    <div ref={barRef} style={{ position: 'relative', padding: '12px 16px 14px', borderTop: `1px solid ${T2.border}`, background: T2.page }}>
      <div style={{
        border: `1px solid ${focused ? T2.accentMid : T2.border}`,
        borderRadius: 14, background: T2.surface,
        boxShadow: focused ? `0 0 0 3px ${T2.accentLight}` : '0 1px 2px rgba(20,24,22,.04)',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        overflow: 'visible',
      }}>
        {/* Agent participants — chip row, mirrors RunDrawer's mentioned-agent chips */}
        {attachedAgents.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px 0', flexWrap: 'wrap' }}>
            {attachedAgents.map(a => (
              <div key={a._id || a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px 3px 6px', borderRadius: 999, background: T2.accentLight, border: `1px solid ${T2.accentMid}`, fontSize: 12, fontWeight: 600, color: T2.accent }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: T2.accentMid, color: T2.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                  {(a.name || a.title || '?')[0].toUpperCase()}
                </div>
                <span>{a.name || a.title}</span>
                <button onMouseDown={e => { e.preventDefault(); onRemoveAgent(a); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: T2.accent, opacity: 0.7, fontSize: 13, lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* Mention dropdown */}
        {suggestions.length > 0 && (
          <div style={{
            position: 'absolute', left: 12, right: 12,
            bottom: mentionAnchor?.bottom ?? 74,
            background: T2.surface, border: `1px solid ${T2.border2}`,
            borderRadius: 12, boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
            zIndex: 100, overflow: 'hidden',
          }}>
            <div style={{ padding: '7px 12px', fontSize: 10.5, color: T2.ink4, borderBottom: `1px solid ${T2.border}`, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Mention</div>
            {suggestions.map(s => (
              <div key={s._id || s.id} onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                onMouseEnter={e => e.currentTarget.style.background = T2.surface2}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: T2.accentMid, color: T2.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
                  {(s.name || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontWeight: 600, color: T2.ink }}>{s.name}</div>
                    {s._type === 'workflow' && <div style={{ fontSize: 11, padding: '2px 6px', borderRadius: 8, background: T2.violetBg, border: `1px solid ${T2.violetBd}`, color: T2.violet }}>workflow</div>}
                    {s._type === 'member' && <div style={{ fontSize: 11, padding: '2px 6px', borderRadius: 8, background: T2.surface2, border: `1px solid ${T2.border}`, color: T2.ink4 }}>member</div>}
                  </div>
                  {s.description && <div style={{ fontSize: 11, color: T2.ink4 }}>{s.description}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Textarea */}
        <textarea
          ref={inputRef}
          placeholder={placeholder || 'Message…'}
          value={value}
          onChange={e => { onChange(e.target.value); adjustHeight(e.target); }}
          onKeyDown={handleKey}
          rows={1}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: 'none', outline: 'none', resize: 'none', background: 'none',
            fontSize: 13, lineHeight: 1.6, color: T2.ink,
            fontFamily: 'inherit',
            padding: '10px 14px 4px',
            overflow: 'auto',
            maxHeight: `${5 * 24}px`,
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onInput={e => adjustHeight(e.target)}
        />

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '5px 10px 8px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button onClick={onAddAgent} title="Mention an agent" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, background: T2.surface2, border: `1px solid ${T2.border}`, cursor: 'pointer', fontSize: 12, color: T2.ink3, fontFamily: 'inherit', fontWeight: 500 }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>+</span>
              <span>Agent</span>
            </button>
            {onModelChange && <ModelPicker value={modelId} onChange={onModelChange} />}
            {picks ? <ToolsPicker picks={picks} /> : (
            <button title="Tools" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, background: T2.surface2, border: `1px solid ${T2.border}`, cursor: 'pointer', fontSize: 12, color: T2.ink3, fontFamily: 'inherit', fontWeight: 500 }}>
              <WrenchIcon size={12} /> + Tools
            </button>)}
            <button onClick={onAttach} title={attachedFilesCount ? `${attachedFilesCount} file(s) attached` : 'Attach file'} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, border: `1px solid ${T2.border}`, background: T2.surface2, cursor: 'pointer', fontSize: 12, color: T2.ink3, fontFamily: 'inherit', fontWeight: 500 }}>
              <PaperclipIcon size={12} /> + Files{attachedFilesCount > 0 ? ` (${attachedFilesCount})` : ''}
            </button>
            {extraTools}
          </div>

          <button
            onClick={onSend}
            disabled={!value.trim() || sending}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 14px', borderRadius: 8,
              background: (!value.trim() || sending) ? T2.border : T2.accent,
              color: (!value.trim() || sending) ? T2.ink4 : 'var(--lyra-color-surface)',
              border: 'none', cursor: (!value.trim() || sending) ? 'default' : 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              transition: 'background 0.15s',
            }}>
            {sending ? 'Sending…' : <><Send size={12} style={{ marginRight: 2 }} /> Send</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ label, onClick, disabled, title }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: '5px 10px', borderRadius: 7,
        border: `1px solid ${T.border}`,
        background: 'white', fontSize: 12, color: disabled ? T.textMuted : T.textMid,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'DM Sans, sans-serif',
      }}>
      {label}
    </button>
  );
}

// ─── Message bubble ───────────────────────────────────────────────────────────
function Bubble({ msg }) {
  // Conversations treats the signed-in user's own assistant as part of their
  // side of the exchange. Only messages from other participants/system roles
  // belong on the left.
  const isMine = msg.role === 'user' || msg.role === 'assistant';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
      {!isMine && (
        <div style={{ fontSize: 11, fontWeight: 700, color: T.green, marginBottom: 3, paddingLeft: 2 }}>
          {msg.agentName || 'Lyra'}
        </div>
      )}
      <MessageAttachments attachments={msg.attachments} mine={isMine} />
      <div style={{
        maxWidth: '80%', padding: '9px 14px',
        borderRadius: isMine ? '14px 14px 4px 14px' : '4px 14px 14px 14px',
        background: isMine ? T.green : T.bgWhite,
        color: isMine ? 'white' : T.text,
        fontSize: 13, lineHeight: 1.6,
        border: isMine ? 'none' : `1px solid ${T.border}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
        dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
      />
      <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3, paddingLeft: 2, paddingRight: 2 }}>
        {timeAgo(msg.ts || msg.createdAt)}
      </div>
    </div>
  );
}

// ─── PersonalFeed ─────────────────────────────────────────────────────────────
// ─── Model select dropdown ── uses AIContext as single source of truth ────────
function ModelSelect({ value, onChange }) {
  const { getEnabledModels, loading } = useAI();
  const models = getEnabledModels();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 11, color: T.textMuted }}>Model</div>
        <div style={{ fontSize: 11, color: value === 'auto' ? T.green : T.textSoft, fontWeight: 500 }}>{value === 'auto' ? 'Auto recommended' : 'Manual selection'}</div>
      </div>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={loading}
        style={{ height: 34, padding: '0 9px', borderRadius: 9, border: `1px solid ${T.border}`, fontSize: 12, color: T.textMid, background: 'var(--lyra-color-surface)', cursor: 'pointer' }}
      >
        <option value="auto">Auto</option>
        {models.map(m => (
          <option key={m.id} value={m.id}>{m.name || m.id}{m.badge ? ` · ${m.badge}` : ''}</option>
        ))}
      </select>
    </div>
  );
}

function PersonalFeed({ sessionId, onOpenContext }) {
  const { selectedModelId, selectedMode } = useAI();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [attachedAgents, setAttachedAgents] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`attachedAgents:personal:${sessionId}`)) || []; } catch { return []; }
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await chatSessionAPI.get(sessionId);
        if (!cancelled) setSession(r.session || r || null);
      } catch { toast.error('Failed to load session'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [session?.messages?.length, sending]);

  const send = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    const optimistic = { role: 'user', content: message.trim(), ts: new Date().toISOString() };
    setSession(prev => ({ ...prev, messages: [...(prev?.messages || []), optimistic] }));
    let outgoing = message.trim();
    setMessage('');
    // Inject pending file content into outgoing
    if (pendingFiles.length > 0) {
      const textFiles = pendingFiles.filter(f => f.content);
      const binFiles = pendingFiles.filter(f => !f.content);
      const parts = [];
      textFiles.forEach(f => parts.push(`[File: ${f.name}]\n${f.content}${f.truncated ? '\n...(content truncated)' : ''}`));
      if (parts.length) outgoing += `\n\n--- Attached Files ---\n${parts.join('\n\n---\n\n')}`;
      if (binFiles.length) outgoing += `\n\n--- Binary Attachments ---\n${binFiles.map(f => `${f.name} (${f.contentType})`).join('\n')}`;
      setPendingFiles([]);
    }
    try {
      const lower = outgoing.toLowerCase();
      const mentions = (attachedAgents || [])
        .filter(a => { const n = (a.name || '').toLowerCase(); return n && lower.includes(`@${n}`); })
        .map(a => ({ agentId: a._id || a.id, name: a.name }));

      // Honour session.model, then fall back to AI Console selection, then auto
      const effectiveModelId = session?.model || (selectedMode === 'manual' ? selectedModelId : null) || 'auto';
      const payload = { content: outgoing, modelId: effectiveModelId };
      if (mentions.length) payload.mentions = mentions;

      const r = await chatSessionAPI.message(sessionId, payload);
      // Replace optimistic + add assistant reply
      const serverMsgs = r?.session?.messages || r?.messages;
      if (serverMsgs) {
        setSession(prev => ({ ...prev, messages: serverMsgs }));
      } else if (r?.reply || r?.message) {
        const reply = { role: 'assistant', content: r.reply || r.message?.content || '', ts: new Date().toISOString() };
        setSession(prev => ({ ...prev, messages: [...(prev?.messages || []), reply] }));
      }
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    const uploadingMsg = { role: 'user', content: `⏳ Uploading ${f.name}…`, ts: new Date().toISOString() };
    setSession(prev => prev ? { ...prev, messages: [...prev.messages, uploadingMsg] } : prev);
    const form = new FormData();
    form.append('file', f);
    try {
      const r = await api.post('/api/attachments/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const meta = r.data?.file || r.data;
      const label = meta.content
        ? `📎 ${meta.originalName} — text extracted (${(meta.size / 1024).toFixed(1)} KB${meta.truncated ? ', truncated' : ''})`
        : `📎 ${meta.originalName} — binary file, will be noted in prompt`;
      setSession(prev => prev ? { ...prev, messages: prev.messages.map(m => m === uploadingMsg ? { ...m, content: label } : m) } : prev);
      setPendingFiles(p => [...p, { name: meta.originalName, contentType: meta.contentType, size: meta.size, content: meta.content, truncated: meta.truncated }]);
    } catch (err) {
      setSession(prev => prev ? { ...prev, messages: prev.messages.filter(m => m !== uploadingMsg) } : prev);
      toast.error(err?.response?.data?.error || err?.message || 'Upload failed');
    }
  };

  const updateModel = async (m) => {
    try {
      await chatSessionAPI.update(sessionId, { model: m });
      setSession(prev => prev ? { ...prev, model: m } : prev);
      toast.success('Model updated');
    } catch { toast.error('Failed to update model'); }
  };

  const saveAttached = (list) => { try { localStorage.setItem(`attachedAgents:personal:${sessionId}`, JSON.stringify(list)); } catch (_) {} };
  const attachAgent = (a) => setAttachedAgents(prev => {
    if (prev.some(x => (x._id || x.id) === (a._id || a.id))) return prev;
    const n = [...prev, a]; saveAttached(n); return n;
  });
  const removeAgent = (a) => setAttachedAgents(prev => { const n = prev.filter(x => (x._id || x.id) !== (a._id || a.id)); saveAttached(n); return n; });

  if (loading) return <LoadingPane />;
  if (!session) return <EmptyPane text="Session not found" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      {/* Header */}
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${T.border}`, background: T.bgWhite, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>{session.title || 'Conversation'}</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>Personal · {(session.messages || []).length} messages</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={onOpenContext} style={{ height: 34, padding: '0 10px', borderRadius: 9, border: `1px solid ${T.border}`, background: 'var(--lyra-color-surface)', color: T.textMid, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
            Context
          </button>
          <ModelSelect value={session.model || 'auto'} onChange={updateModel} />
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {(session.messages || []).length === 0 ? (
          <div style={{ textAlign: 'center', marginTop: 90, color: T.textMuted, maxWidth: 620, marginInline: 'auto' }}>
            <div style={{ fontSize: 30, lineHeight: 1.15, fontWeight: 600, color: T.text, letterSpacing: '-0.01em' }}>What can I help you accomplish?</div>
            <div style={{ fontSize: 13, marginTop: 10, color: T.textSoft }}>Plan a project · Research something · Analyze data · Work on your repository</div>
          </div>
        ) : (
          (session.messages || []).map((m, i) => <Bubble key={i} msg={m} />)
        )}
        {sending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: T.green, opacity: 0.6,
                  animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>
            <span style={{ fontSize: 12, color: T.textMuted }}>Lyra is thinking…</span>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      <ComposeBar
        value={message} onChange={setMessage} onSend={send} sending={sending}
        placeholder={attachedAgents.length ? `Message… type @${attachedAgents[0].name} to mention` : 'Ask Lyra anything… use @ to mention an agent'}
        attachedAgents={attachedAgents}
        onAddAgent={() => setPickerOpen(true)}
        onRemoveAgent={removeAgent}
        onMentionAgent={attachAgent}
        onOpenAgent={a => window.dispatchEvent(new CustomEvent('lyra:open-agent', { detail: a }))}
        onAttach={handleAttachClick}
        attachedFilesCount={pendingFiles.length}
      />

      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />

      <AgentPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={attachAgent} />
      <style>{`@keyframes pulse { 0%,100%{transform:scale(0.8);opacity:0.4} 50%{transform:scale(1.2);opacity:1} }`}</style>
    </div>
  );
}

// ─── SpaceFeed ────────────────────────────────────────────────────────────────
function SpaceFeed({ spaceId, onOpenContext, embedded = false }) {
  const [space, setSpace] = useState(null);
  const [activities, setActivities] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sseConnected, setSseConnected] = useState(false);
  // Agents chosen for the message being written. They apply to that message only: they are cleared once it is sent and are never
  // restored from the browser, so an old choice cannot silently steer every later message (the team answers unmentioned ones).
  const [attachedAgents, setAttachedAgents] = useState([]);
  // Names of this person's own agents, so their replies sit on their side of the thread.
  const [myAgentNames, setMyAgentNames] = useState(() => new Set());
  useEffect(() => {
    let live = true;
    customAgentAPI.list().then((r) => {
      if (!live) return;
      const list = Array.isArray(r) ? r : r?.agents || [];
      setMyAgentNames(new Set(list.filter((a) => !a.isLyran).map((a) => normalizeIdentity(a.name)).filter(Boolean)));
    }).catch(() => {});
    return () => { live = false; };
  }, [spaceId]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const picks = useComposerPicks();
  const [modelId, setModelId] = useState('auto');
  const fileInputRef = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  // Persisted SpaceFile ids for files attached to the message being composed —
  // attached to the SpaceActivity on send so they also appear in the Files tab.
  const [pendingFileIds, setPendingFileIds] = useState([]);
  const feedRef = useRef(null);
  const meId = getUserIdFromToken();
  // Who this person is talking to (a shared agent, via Use / a chip / an @mention), and what the team has shared.
  const { session, start: startSession, clear: endSession, touch: touchSession, mentionFor } = useAgentSession(spaceId);
  const roster = useRoster(spaceId, false);
  const sharedAgents = useMemo(() => (roster?.agents || []).filter((a) => a.status === 'active'), [roster]);
  // An agent that stops being shared (blocked, removed) ends the conversation instead of leaving it pointing nowhere.
  useEffect(() => {
    if (session && roster && !sharedAgents.some((a) => String(a.agentId) === String(session.agentId))) {
      endSession();
      toast(`${session.name} is no longer shared, so you are back to the whole team.`);
    }
  }, [session, roster, sharedAgents, endSession]);
  // lyran_message activities (including auto-pilot replies) always carry
  // userId: null — they're attributed by lyranName instead — so "is this my
  // own Lyran talking" needs a name lookup via my own space membership.
  const myLyranName = useMemo(() => {
    const mine = (space?.members || []).find(m => String(m.userId) === String(meId));
    return mine?.lyranName || null;
  }, [space?.members, meId]);
  const myAssistantNames = myAgentNames;
  const activitiesRef = useRef(activities);
  useEffect(() => { activitiesRef.current = activities; }, [activities]);

  // "X is getting started…" status activities are transient — shown as a
  // typing indicator below the feed, never as a permanent chat bubble. A
  // recency window guards against any stale/orphaned one (e.g. from before
  // this was fixed server-side, or an edge case that isn't cleaned up)
  // being shown as "still typing" forever.
  const TYPING_WINDOW_MS = 45_000;
  // People typing right now (from the room's live stream), each for a few seconds after their last keystroke.
  const [typingPeople, setTypingPeople] = useState({});
  useEffect(() => {
    const id = setInterval(() => setTypingPeople((prev) => {
      const now = Date.now(); const next = {};
      for (const [k, v] of Object.entries(prev)) if (now - v.at < 6000) next[k] = v;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    }), 1500);
    return () => clearInterval(id);
  }, []);
  const typingNames = useMemo(() => {
    const now = Date.now();
    const agents = activities
      .filter(a => a.toolCall?.status === 'running' && a.createdAt && (now - new Date(a.createdAt).getTime()) < TYPING_WINDOW_MS)
      .map(a => a.lyranName)
      .filter(Boolean);
    const people = Object.entries(typingPeople).filter(([id]) => String(id) !== String(meId)).map(([, v]) => v.name).filter(Boolean);
    return [...new Set([...people, ...agents])];
  }, [activities, typingPeople, meId]);

  // Tell the team we are typing (at most every 3s) so Lyrans wait instead of talking over us.
  const lastTypingPing = useRef(0);
  const handleMessageChange = useCallback((value) => {
    setMessage(value);
    if (value && Date.now() - lastTypingPing.current > 3000) { lastTypingPing.current = Date.now(); spacesAPI.typing(spaceId); }
  }, [spaceId]);
  const visibleActivities = useMemo(
    () => activities.filter(a => a.toolCall?.status !== 'running'),
    [activities]
  );

  useEffect(() => {
    let cancelled = false;
    // Load initial space metadata and recent activities once when opening the feed
    (async () => {
      setLoading(true);
      try {
        const [spaceRes, actRes] = await Promise.all([
          spacesAPI.get(spaceId),
          spacesAPI.getActivity(spaceId, { limit: 50 }).catch(() => ({ activities: [] })),
        ]);
        if (!cancelled) { setSpace(spaceRes?.space || null); setActivities(actRes?.activities || []); }
      } catch { toast.error('Failed to load space'); }
      if (!cancelled) setLoading(false);
    })();

    const mergeActivities = (incoming = []) => {
      setActivities(prev => {
        const next = [...prev];
        for (const item of incoming) {
          if (item?._id && !next.some(a => a._id === item._id)) next.push(item);
        }
        return next.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      });
    };

    const refreshLatestActivities = async () => {
      try {
        const res = await spacesAPI.getActivity(spaceId, { limit: 50 });
        if (!cancelled) {
          const latest = res?.activities || [];
          mergeActivities(latest);
          // The server deletes a Lyran's temporary "is getting started…" row when its reply lands, but this list only ever grows.
          // Drop any such row the server no longer has, so "X is typing…" ends with the reply.
          const alive = new Set(latest.map(a => String(a._id)));
          setActivities(prev => prev.filter(a => a.toolCall?.status !== 'running' || alive.has(String(a._id)) || !a.createdAt || (Date.now() - new Date(a.createdAt).getTime()) < 4000));
        }
      } catch (_) {}
    };

    // Subscribe to SSE for live updates, with polling as a safety net when the stream drops.
    const token = getAuthToken();
    let es = null;
    let poll = null;
    try {
      es = new EventSource(`/api/spaces/${spaceId}/stream?token=${encodeURIComponent(token || '')}`);
      es.onmessage = (e) => {
        try {
          const { event, payload } = JSON.parse(e.data);
          if (event === 'connected') setSseConnected(true);
          if (event === 'activity_new' && payload) mergeActivities([payload]);
          if (event === 'roster_update') window.dispatchEvent(new Event('lyra:roster-update'));   // cards in the thread re-read the roster
          if (event === 'activity_removed' && Array.isArray(payload?.ids)) { const gone = new Set(payload.ids.map(String)); setActivities(prev => prev.filter(a => !gone.has(String(a._id)))); }
          if (event === 'typing' && payload?.userId) setTypingPeople(prev => ({ ...prev, [payload.userId]: { name: payload.name, at: Date.now() } }));
          if (event === 'space_update' && payload) setSpace(prev => prev ? { ...prev, ...payload } : prev);
        } catch (_) {}
      };
      es.onopen = () => setSseConnected(true);
      es.onerror = () => {
        setSseConnected(false);
        // Keep the EventSource alive so the browser can auto-reconnect.
      };
    } catch (err) {
      console.warn('SSE for space feed unavailable', err);
      setSseConnected(false);
    }

    poll = setInterval(refreshLatestActivities, 3000);

    return () => {
      cancelled = true;
      try { es?.close(); } catch (_) {}
      if (poll) clearInterval(poll);
    };
  }, [spaceId]);

  useEffect(() => { if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight; }, [activities.length]);

  const send = async () => {
    if (!message.trim()) return;
    let outgoing = message.trim();
    setMessage('');
    // Inject pending files into outgoing
    if (pendingFiles.length > 0) {
      const textFiles = pendingFiles.filter(f => f.content);
      const binFiles = pendingFiles.filter(f => !f.content);
      const parts = [];
      textFiles.forEach(f => parts.push(`[File: ${f.name}]\n${f.content}${f.truncated ? '\n...(content truncated)' : ''}`));
      if (parts.length) outgoing += `\n\n--- Attached Files ---\n${parts.join('\n\n---\n\n')}`;
      if (binFiles.length) outgoing += `\n\n--- Binary Attachments ---\n${binFiles.map(f => `${f.name} (${f.contentType})`).join('\n')}`;
      setPendingFiles([]);
    }
    try {
      const attachedMentions = (attachedAgents || []).map(a => ({ agentId: a._id || a.id, name: a.name }));
      const explicitMentions = (attachedAgents || [])
        .filter(a => { const n = (a.name || '').toLowerCase(); return n && outgoing.toLowerCase().includes(`@${n}`); })
        .map(a => ({ agentId: a._id || a.id, name: a.name }));
      const mentions = explicitMentions.length
        ? [
            ...explicitMentions,
            ...attachedMentions.filter(a => !explicitMentions.some(m => String(m.agentId) === String(a.agentId))),
          ]
        : attachedMentions;
      // No @mention and no chip: a message in a conversation with an agent goes to that agent (the pill above the box says so).
      const viaConversation = mentions.length ? [] : mentionFor(outgoing);
      const payload = { content: outgoing, modelId };
      if (picks.skillIds.length) payload.skillIds = picks.skillIds;
      if (mentions.length) payload.mentions = mentions;
      else if (viaConversation.length) payload.mentions = viaConversation;
      if (pendingFileIds.length) payload.fileIds = pendingFileIds;
      const res = await spacesAPI.postMessage(spaceId, payload);
      setPendingFileIds([]);
      setAttachedAgents([]);          // the chosen agents were for this message only
      // @mentioning a shared agent starts a conversation with it; any message keeps the current one alive.
      const named = !session ? typedAgentIn(outgoing, sharedAgents) : null;
      if (named) startSession({ agentId: named.agentId, name: named.name, access: named.access, payer: named.payer, owner: named.ownerName, ownerUserId: named.ownerUserId });
      else touchSession();
      if (res?.activity) setActivities(prev => prev.some(a => a._id === res.activity._id) ? prev : [...prev, res.activity]);
    } catch { toast.error('Failed to send'); }
  };

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    const uploading = { _id: `upload-${Date.now()}` };
    setActivities(prev => [...prev, { _id: uploading._id, type: 'system', userId: meId, content: `⏳ Uploading ${f.name}…`, createdAt: new Date().toISOString() }]);
    const form = new FormData(); form.append('file', f);
    try {
      // Persisted upload — this is the copy that shows in the Files tab and
      // gets attached to the message once sent (see spacesAPI.uploadFile).
      // Only a canManage member can actually persist it; other members'
      // uploads still work for the text-extraction/context path below.
      let persistedFileId = null;
      try {
        const persisted = await spacesAPI.uploadFile(spaceId, f);
        persistedFileId = persisted?.file?._id || null;
        if (persistedFileId) setPendingFileIds(p => [...p, persistedFileId]);
      } catch (_) { /* non-fatal — sender may not have manager/owner role; text-extraction below still works */ }

      const r = await api.post('/api/attachments/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const meta = r.data?.file || r.data;
      const label = meta.content ? `📎 ${meta.originalName} — text extracted (${(meta.size / 1024).toFixed(1)} KB${meta.truncated ? ', truncated' : ''})` : `📎 ${meta.originalName} — binary file`;
      setActivities(prev => prev.map(a => a._id === uploading._id ? { ...a, content: label } : a));
      setPendingFiles(p => [...p, { name: meta.originalName, contentType: meta.contentType, size: meta.size, content: meta.content, truncated: meta.truncated }]);
    } catch (err) {
      setActivities(prev => prev.filter(a => a._id !== uploading._id));
      toast.error(err?.response?.data?.error || err?.message || 'Upload failed');
    }
  };

  const attachAgent = (a) => setAttachedAgents(prev => (prev.some(x => (x._id||x.id) === (a._id||a.id)) ? prev : [...prev, a]));
  const removeAgent = (a) => setAttachedAgents(prev => prev.filter(x => (x._id||x.id) !== (a._id||a.id)));
  const sharedMentionOptions = useMemo(() => sharedAgents.map((a) => ({
    _id: String(a.agentId), id: String(a.agentId), name: a.name, _type: 'agent',
    description: `${String(a.ownerUserId) === String(meId) ? 'Yours' : `Shared by ${a.ownerName}`} · ${a.access === 'open' ? 'open to use' : 'owner approves first'}`,
  })), [sharedAgents, meId]);
  const memberMentionOptions = useMemo(() => (space?.members || [])
    .filter(m => m.lyranAgentId && m.lyranName)
    .map(m => ({
      _id: String(m.lyranAgentId),
      id: String(m.lyranAgentId),
      name: m.lyranName,
      description: 'Space member',
      _type: 'member',
      memberUserId: m.userId,
      lyranAvatar: m.lyranAvatar,
    })), [space?.members]);

  if (loading) return <LoadingPane />;
  if (!space) return <EmptyPane text="Space not found" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T2.page }}>
      {/* When embedded in SpaceDetailsPage, the space name/description/member
          count is already shown above the tabs, so this header would just
          duplicate it — only render it in standalone contexts (the /chats
          hub, RunDrawer's floating space drawer). */}
      {!embedded && (
        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${T2.border}`, background: T2.surface, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: T2.ink }}>{space.name}</div>
            <div style={{ fontSize: 11, color: T2.ink4 }}>
              {space.visibility === 'public' ? '🌐 Public' : '🔒 Private'} · {space.memberCount || (space.members || []).length} members
              {sseConnected && <span style={{ color: T2.green }}> · ● Live</span>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onOpenContext && (
              <button onClick={onOpenContext} style={{ height: 34, padding: '0 10px', borderRadius: 9, border: `1px solid ${T2.border}`, background: T2.surface, color: T2.ink2, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                Context
              </button>
            )}
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: sseConnected ? T2.green : T2.ink4 }} title={sseConnected ? 'Live' : 'Reconnecting…'} />
          </div>
        </div>
      )}

      <div ref={feedRef} style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {activities.length === 0 ? (
          <div style={{ textAlign: 'center', marginTop: 70, color: T2.ink4, maxWidth: 560, marginInline: 'auto' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T2.accent, marginBottom: 10 }}>Ready when you are</div>
            <div style={{ fontSize: 23, fontWeight: 650, letterSpacing: '-0.02em', color: T2.ink, marginBottom: 8 }}>What should Lyra handle in this space?</div>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: T2.ink3 }}>Assign an agent, attach files, and collaborate in one thread.</div>
          </div>
        ) : visibleActivities.map((a, i) => {
          if (a.type === 'agent_event') return <AgentEventRow key={a._id || i} activity={a} spaceId={spaceId} meId={meId} />;
          if (a.type === 'agent_directory') return <DirectoryCard key={a._id || i} activity={a} spaceId={spaceId} meId={meId} />;
          if (a.type === 'agent_request') return <RequestCard key={a._id || i} activity={a} spaceId={spaceId} meId={meId} />;
          if (a.type === 'agent_sponsor') return <SponsorCard key={a._id || i} activity={a} spaceId={spaceId} meId={meId} />;
          const isMyUser = !!a.userId && String(a.userId) === String(meId);
          const speakerName = normalizeIdentity(a.lyranName);
          const isMyLyran = a.type === 'lyran_message'
            && !!myLyranName
            && speakerName === normalizeIdentity(myLyranName);
          const isMyAssistant = a.type === 'lyran_message' && myAssistantNames.has(speakerName);
          const isMe = isMyUser || isMyLyran || isMyAssistant;
          // A person's message shows the person's name; a Lyran's shows the Lyran's.
          const displayName = isMyUser ? 'You' : (a.type === 'human_message' && a.meta?.senderName ? a.meta.senderName : (a.lyranName || (isMe ? 'Assistant' : 'Member')));
          return (
            <ChatBubble
              key={a._id || i}
              align={isMe ? 'right' : 'left'}
              filled={isMyUser}
              name={displayName}
              via={a.meta?.via || ''}
              paidBy={a.meta?.payerKind && a.meta.payerKind !== 'owner' ? (a.meta.payerName || '') : ''}
              content={a.content}
              time={a.createdAt ? new Date(a.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
              attachments={<MessageAttachments attachments={a.attachments} mine={isMe} />}
            />
          );
        })}
      </div>

      {typingNames.length > 0 && (
        <div style={{ padding: '6px 24px', display: 'flex', alignItems: 'center', gap: 8, color: T2.ink3, fontSize: 12, fontStyle: 'italic', borderTop: `1px solid ${T2.border}` }}>
          <span style={{ display: 'inline-flex', gap: 3 }}>
            {[0, 1, 2].map(n => (
              <span key={n} style={{ width: 4, height: 4, borderRadius: '50%', background: T2.accent, opacity: 0.45, animation: `pulse 1.2s ease-in-out ${n * 0.18}s infinite` }} />
            ))}
          </span>
          {typingNames.join(', ')} {typingNames.length > 1 ? 'are' : 'is'} typing…
        </div>
      )}

      <AgentSessionPill session={session} grants={roster?.grants} covers={roster?.covers} meId={meId} onEnd={endSession} />
      <TeamAgentStrip spaceId={spaceId} meId={meId} hidden={!!session} />
      <ComposeBar
        value={message} onChange={handleMessageChange} onSend={send} sending={false}
        placeholder={session ? `Message ${session.name}…` : `Message space… use @agent to direct a request`}
        attachedAgents={attachedAgents}
        onAddAgent={() => setPickerOpen(true)}
        onRemoveAgent={removeAgent}
        onMentionAgent={attachAgent}
        mentionOptions={[...memberMentionOptions, ...sharedMentionOptions]}
        onOpenAgent={a => window.dispatchEvent(new CustomEvent('lyra:open-agent', { detail: a }))}
        onAttach={handleAttachClick}
        attachedFilesCount={pendingFiles.length}
        picks={picks}
        modelId={modelId}
        onModelChange={setModelId}
        extraTools={<ShareAgentMenu spaceId={spaceId} meId={meId} />}
      />
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
      <AgentPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={attachAgent} extraItems={memberMentionOptions} />
    </div>
  );
}

// ─── Utilities ────────────────────────────────────────────────────────────────
function LoadingPane() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.textMuted, fontSize: 13 }}>
      Loading…
    </div>
  );
}
function EmptyPane({ text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.textMuted, fontSize: 13 }}>
      {text}
    </div>
  );
}

export { SpaceFeed };

// ─── Context menu (Rename / Move to folder / Delete) ─────────────────────────
function ChatContextMenu({ sessionId, currentTitle, folders, onClose, onRenamed, onMoved, onDeleted }) {
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(currentTitle);
  const menuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const doRename = async () => {
    if (!renameVal.trim() || renameVal === currentTitle) { onClose(); return; }
    try {
      await chatSessionAPI.update(sessionId, { title: renameVal.trim() });
      onRenamed(sessionId, renameVal.trim());
      toast.success('Renamed');
    } catch { toast.error('Failed to rename'); }
    onClose();
  };

  const doMove = async (folderId) => {
    try {
      await chatSessionAPI.update(sessionId, { folderId: folderId === '__none__' ? null : folderId });
      onMoved(sessionId, folderId);
      toast.success('Moved');
    } catch { toast.error('Failed to move'); }
    onClose();
  };

  const doDelete = async () => {
    if (!window.confirm('Delete this chat?')) return;
    try {
      await chatSessionAPI.remove(sessionId);
      onDeleted(sessionId);
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
    onClose();
  };

  const menuStyle = {
    position: 'absolute', right: 0, top: '100%', zIndex: 200,
    background: T.bgWhite, border: `1px solid ${T.border}`,
    borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
    minWidth: 190, overflow: 'hidden', marginTop: 4,
  };
  const itemStyle = (danger) => ({
    padding: '9px 14px', cursor: 'pointer', fontSize: 13,
    color: danger ? T.red : T.text, display: 'flex', alignItems: 'center', gap: 8,
  });
  const dividerStyle = { padding: '5px 14px 3px', fontSize: 10, fontWeight: 700,
    color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em',
    borderTop: `1px solid ${T.borderLight}`, marginTop: 2 };

  return (
    <div ref={menuRef} style={menuStyle}>
      {renaming ? (
        <div style={{ padding: '10px 12px' }}>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6, fontWeight: 600 }}>Rename</div>
          <input
            autoFocus
            value={renameVal}
            onChange={e => setRenameVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') doRename(); if (e.key === 'Escape') onClose(); }}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 7, border: `1px solid ${T.green}`, fontSize: 13, outline: 'none' }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button onClick={doRename} style={{ flex: 1, padding: '5px 0', borderRadius: 7, border: 'none', background: T.green, color: 'white', fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>Save</button>
            <button onClick={onClose} style={{ flex: 1, padding: '5px 0', borderRadius: 7, border: `1px solid ${T.border}`, background: 'white', fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div
            style={itemStyle(false)}
            onMouseEnter={e => e.currentTarget.style.background = T.borderLight}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={() => setRenaming(true)}>
            ✏️ Rename
          </div>

          <div style={dividerStyle}>Move to folder</div>
          <div
            style={itemStyle(false)}
            onMouseEnter={e => e.currentTarget.style.background = T.borderLight}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={() => doMove('__none__')}>
            <span style={{ opacity: 0.5 }}>◻</span> Uncategorized
          </div>
          {folders.filter(f => f.id !== '__uncategorized__' && f.id !== '__all__').map(f => (
            <div key={f.id}
              style={itemStyle(false)}
              onMouseEnter={e => e.currentTarget.style.background = T.borderLight}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => doMove(f.id)}>
              <span style={{ width: 14, height: 14, borderRadius: 3, background: T.green, display: 'inline-block' }} /> {f.name}
            </div>
          ))}

          <div style={{ borderTop: `1px solid ${T.borderLight}`, marginTop: 2 }}>
            <div
              style={itemStyle(true)}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--lyra-color-danger-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={doDelete}>
              🗑 Delete
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Exported so other chat surfaces (e.g. the New Task page's history rail)
// can reuse the same rename/move/delete menu instead of rebuilding it.
export { ChatContextMenu };

// ─── Sidebar conversation item ────────────────────────────────────────────────
function ConvItem({ icon, title, sub, time, active, onClick, badge, sessionId, folders, onRenamed, onMoved, onDeleted, isSpace, raw, pinned, onTogglePin }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); }}
    >
      <div
        onClick={onClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 10px', borderRadius: 10, cursor: 'pointer',
          marginBottom: 2,
          background: active ? T.greenLight : (hovered ? T.borderLight : 'transparent'),
          border: active ? `1px solid ${alpha(T.green, '30')}` : '1px solid transparent',
          transition: 'all 0.1s',
        }}
      >
        <div style={{ width: 34, height: 34, borderRadius: 9, background: active ? T.green : 'var(--app-surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0, color: active ? 'white' : T.textSoft }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: active ? 700 : 500, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
          {sub && <div style={{ fontSize: 11, color: T.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
        </div>
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          {time && !hovered && <div style={{ fontSize: 10, color: T.textMuted }}>{time}</div>}
          {/* ··· button — only for personal chats, on hover */}
          {hovered && (
            <button
              onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
              style={{ width: 24, height: 24, borderRadius: 6, border: 'none', background: active ? `${alpha(T.green, '30')}` : T.border, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: T.textSoft, lineHeight: 1 }}>
              ···
            </button>
          )}
          {badge > 0 && <div style={{ width: 18, height: 18, borderRadius: '50%', background: T.green, color: 'white', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{badge}</div>}
        </div>
      </div>

      {menuOpen && !isSpace && (
        <ChatContextMenu
          sessionId={sessionId}
          currentTitle={title}
          folders={folders || []}
          onClose={() => setMenuOpen(false)}
          onRenamed={onRenamed}
          onMoved={onMoved}
          onDeleted={onDeleted}
        />
      )}

      {menuOpen && isSpace && (
        <SpaceContextMenu
          spaceId={sessionId}
          spaceRaw={raw}
          onClose={() => setMenuOpen(false)}
          onDeleted={onDeleted}
          pinned={pinned}
          onTogglePin={onTogglePin}
        />
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ConversationsPage() {
  const [folders, setFolders] = useState([]);         // [{id, name, sessions:[]}]
  const [allSessions, setAllSessions] = useState([]); // flat list for new-chat optimistic update
  const [spaces, setSpaces] = useState([]);
  const [pinnedSpaces, setPinnedSpaces] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pinnedSpaces')) || []; } catch { return []; }
  });
  const [folderOpen, setFolderOpen] = useState({});   // { folderId: bool }
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const MOBILE_BP = 900;
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BP : false));
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= MOBILE_BP;
      setIsMobile(mobile);
      if (!mobile) setMobileSidebarOpen(false);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isMobile) setMobileSidebarOpen(false);
  }, [isMobile, selected?.id]);

  useEffect(() => {
    setContextOpen(false);
  }, [selected?.id, selected?.type]);

  // ── Load data ──────────────────────────────────────────────────────────────
  const reload = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [sessionsRes, foldersRes, spacesRes] = await Promise.all([
        chatSessionAPI.list(),
        chatSessionAPI.listFolders().catch(() => null),
        spacesAPI.list({ filter: 'all', limit: 200 }).catch(() => ({ spaces: [] })),
      ]);

      // FIX: normalize sessions — handle both array and {sessions:[]} shape
      const rawSessions = Array.isArray(sessionsRes)
        ? sessionsRes
        : (sessionsRes?.sessions || []);
      const normalized = rawSessions.map(s => ({
        id: s._id || s.id,
        title: s.title || 'Untitled',
        model: s.model || 'auto',
        updatedAt: s.updatedAt || s.createdAt,
        folderId: s.folderId || null,
        raw: s,
      }));
      setAllSessions(normalized);

      // FIX: build folders properly
      let foldersList = [];
      const rawFolders = foldersRes?.folders || (Array.isArray(foldersRes) ? foldersRes : null);

      if (rawFolders && rawFolders.length > 0) {
        // Map folder sessions: items may be IDs or full objects
        foldersList = rawFolders.map(folder => {
          const folderSessions = (folder.sessions || folder.items || []).map(item => {
            if (typeof item === 'string' || typeof item === 'object' && item._id === undefined && item.id === undefined) {
              // item is an ID string
              return normalized.find(s => s.id === item);
            }
            // item is an object
            const id = item._id || item.id;
            return normalized.find(s => s.id === id) || { id, title: item.title || 'Untitled', raw: item };
          }).filter(Boolean);
          return { id: folder._id || folder.id, name: folder.name || folder.label || 'Folder', sessions: folderSessions };
        });

        // FIX: sessions not in any folder → put in a default "My Chats" group
        const inFolder = new Set(foldersList.flatMap(f => f.sessions.map(s => s.id)));
        const uncategorized = normalized.filter(s => !inFolder.has(s.id));
        if (uncategorized.length) foldersList.unshift({ id: '__uncategorized__', name: 'My Chats', sessions: uncategorized });
      } else {
        // No folders API — dump all sessions into one group
        foldersList = [{ id: '__all__', name: 'My Chats', sessions: normalized }];
      }

      setFolders(foldersList);

      // Initialize all folders open by default
      setFolderOpen(prev => {
        const next = { ...prev };
        foldersList.forEach(f => { if (next[f.id] === undefined) next[f.id] = true; });
        return next;
      });

      // Only include spaces the user is a member of (or owner)
      const spaceItems = (spacesRes?.spaces || []).filter(sp => sp.isMember || sp.isOwner).map(sp => ({
        id: sp._id || sp.id, title: sp.name, type: 'space',
        visibility: sp.visibility,
        members: sp.memberCount || (sp.members || []).length,
        lastActivity: sp.lastActiveAt || sp.updatedAt || sp.createdAt,
        raw: sp,
      }));
      // reorder using pinnedSpaces (most-recent first)
      const reorder = (list, pinned) => {
        const pinnedSet = new Set(pinned);
        const pinnedOrdered = pinned.map(id => list.find(s => String(s.id) === String(id))).filter(Boolean);
        const others = list.filter(s => !pinnedSet.has(String(s.id)) && !pinnedSet.has(s.id));
        return [...pinnedOrdered, ...others];
      };
      setSpaces(reorder(spaceItems, pinnedSpaces));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load conversations');
    }
    if (!silent) setLoading(false);
  }, [pinnedSpaces]);

  useEffect(() => {
    reload();
    const handler = () => reload(true);
    window.addEventListener('chats:updated', handler);
    return () => window.removeEventListener('chats:updated', handler);
  }, [reload]);

  // If the route contains a spaceId (e.g. /spaces/:spaceId), auto-select that space
  const params = useParams();
  const routeSpaceId = params?.spaceId;

  useEffect(() => {
    if (!routeSpaceId) return;
    // if already loaded in spaces list, select immediately
    const found = spaces.find(s => String(s.id) === String(routeSpaceId));
    if (found) {
      setSelected({ id: found.id, title: found.title, type: 'space', raw: found.raw });
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const sp = await spacesAPI.get(routeSpaceId);
        if (!mounted || !sp) return;
        const spItem = { id: sp._id || sp.id, title: sp.name || sp.title || 'Space', type: 'space', raw: sp, members: sp.memberCount || (sp.members || []).length, visibility: sp.visibility };
        setSelected({ id: spItem.id, title: spItem.title, type: 'space', raw: spItem.raw });
        setSpaces(prev => prev.find(p => String(p.id) === String(spItem.id)) ? prev : [spItem, ...prev]);
      } catch (err) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, [routeSpaceId, spaces]);

  // ── Rename / Move / Delete handlers ───────────────────────────────────────
  const handleRenamed = useCallback((sessionId, newTitle) => {
    setFolders(prev => prev.map(f => ({
      ...f, sessions: f.sessions.map(s => s.id === sessionId ? { ...s, title: newTitle } : s),
    })));
    setSelected(prev => prev?.id === sessionId ? { ...prev, title: newTitle } : prev);
  }, []);

  const handleMoved = useCallback((sessionId, targetFolderId) => {
    setFolders(prev => {
      const session = prev.flatMap(f => f.sessions).find(s => s.id === sessionId);
      if (!session) return prev;
      const without = prev.map(f => ({ ...f, sessions: f.sessions.filter(s => s.id !== sessionId) }));
      if (targetFolderId === '__none__') {
        const idx = without.findIndex(f => f.id === '__uncategorized__' || f.id === '__all__');
        if (idx >= 0) without[idx] = { ...without[idx], sessions: [session, ...without[idx].sessions] };
        else without.unshift({ id: '__uncategorized__', name: 'My Chats', sessions: [session] });
        return without;
      }
      const idx = without.findIndex(f => f.id === targetFolderId);
      if (idx >= 0) without[idx] = { ...without[idx], sessions: [session, ...without[idx].sessions] };
      return without;
    });
  }, []);

  const handleDeleted = useCallback((sessionId) => {
    setFolders(prev => prev.map(f => ({ ...f, sessions: f.sessions.filter(s => s.id !== sessionId) })));
    setSelected(prev => prev?.id === sessionId ? null : prev);
  }, []);

  const handleSpaceDeleted = useCallback((spaceId) => {
    setSpaces(prev => prev.filter(s => String(s.id) !== String(spaceId)));
    setSelected(prev => prev?.id === spaceId ? null : prev);
  }, []);

  // Pin management (client-side persistent via localStorage)
  const savePinned = (arr) => {
    try { localStorage.setItem('pinnedSpaces', JSON.stringify(arr)); } catch (_) {}
  };

  const reorderSpacesByPinned = (list, pinned) => {
    const pinnedSet = new Set(pinned.map(id => String(id)));
    const pinnedOrdered = pinned.map(id => list.find(s => String(s.id) === String(id))).filter(Boolean);
    const others = list.filter(s => !pinnedSet.has(String(s.id)));
    return [...pinnedOrdered, ...others];
  };

  const togglePin = useCallback((spaceId) => {
    setPinnedSpaces(prev => {
      let next = prev.slice();
      const idx = next.findIndex(id => String(id) === String(spaceId));
      if (idx >= 0) {
        // unpin
        next.splice(idx, 1);
      } else {
        // pin: add to front (most-recent first)
        next = [String(spaceId), ...next.filter(id => String(id) !== String(spaceId))];
        // enforce max 3
        if (next.length > 3) next = next.slice(0, 3);
      }
      savePinned(next);
      // reorder spaces in UI
      setSpaces(prevSpaces => reorderSpacesByPinned(prevSpaces, next));
      return next;
    });
  }, []);

  // ── New chat ───────────────────────────────────────────────────────────────
  const handleNewChat = async () => {
    try {
      const r = await chatSessionAPI.create();
      const raw = r?.session || r;
      const newSession = {
        id: raw?._id || raw?.id,
        title: raw?.title || 'New Chat',
        model: raw?.model || 'auto',
        updatedAt: raw?.updatedAt || new Date().toISOString(),
        folderId: null,
        raw,
      };
      // FIX: optimistically prepend into first folder
      setAllSessions(prev => [newSession, ...prev]);
      setFolders(prev => {
        if (prev.length === 0) return [{ id: '__all__', name: 'My Chats', sessions: [newSession] }];
        const updated = [...prev];
        // First folder (uncategorized or default) gets the new chat prepended
        const targetIdx = updated.findIndex(f => f.id === '__uncategorized__' || f.id === '__all__') ?? 0;
        const idx = targetIdx >= 0 ? targetIdx : 0;
        updated[idx] = { ...updated[idx], sessions: [newSession, ...updated[idx].sessions] };
        return updated;
      });
      setFolderOpen(prev => ({ ...prev, '__uncategorized__': true, '__all__': true }));
      setSelected({ id: newSession.id, title: newSession.title, type: 'personal', raw: newSession.raw });
    } catch { toast.error('Failed to create chat'); }
  };

  // ── New folder ───────────────────────────────────────────────────────────
  const createFolder = async () => {
    const name = window.prompt('Folder name:');
    if (!name?.trim()) return;
    try {
      const r = await chatSessionAPI.createFolder({ name: name.trim() });
      // r.folder or r
      const folder = r?.folder || r;
      setFolders(prev => [...prev, { id: folder._id || folder.id, name: folder.name || folder.label || name, sessions: folder.sessions || [] }]);
      try { window.dispatchEvent(new CustomEvent('chats:updated')); } catch (_) {}
    } catch { toast.error('Failed to create folder'); }
  };

  // ── Filter + search ────────────────────────────────────────────────────────
  const q = search.toLowerCase().trim();

  const filteredFolders = (filter === 'spaces') ? [] : folders.map(f => ({
    ...f,
    sessions: q ? f.sessions.filter(s => s.title.toLowerCase().includes(q)) : f.sessions,
  })).filter(f => f.sessions.length > 0 || !q);

  const filteredSpaces = (filter === 'personal') ? [] : (q
    ? spaces.filter(s => s.title.toLowerCase().includes(q))
    : spaces);

  const totalChats = folders.reduce((a, f) => a + f.sessions.length, 0);

  return (
    <div style={{ display: 'flex', height: isMobile ? '100%' : '93vh', background: T.bg, fontFamily: 'DM Sans, sans-serif', position: 'relative' }}>

      <style>{`
        @media (max-width: 900px) {
          .conv-mobile-sidebar {
            position: fixed !important;
            top: var(--lyra-mobile-header-height, 56px) !important;
            left: 0 !important;
            bottom: 0 !important;
            width: min(86vw, 320px) !important;
            z-index: 80 !important;
            transform: translateX(-105%);
            transition: transform 0.24s ease;
            box-shadow: 8px 0 24px rgba(0,0,0,0.16);
          }
          .conv-mobile-sidebar.open {
            transform: translateX(0);
          }
          .conv-main-pane {
            width: 100%;
          }
        }
      `}</style>

      {isMobile && mobileSidebarOpen && (
        <div
          onClick={() => setMobileSidebarOpen(false)}
          style={{ position: 'fixed', top: 'var(--lyra-mobile-header-height, 56px)', left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.22)', zIndex: 70 }}
        />
      )}

      {/* ── Sidebar ── */}
      <div className={`conv-mobile-sidebar ${isMobile && mobileSidebarOpen ? 'open' : ''}`} style={{ width: css.sidebarWidth, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', background: T.bgWhite, flexShrink: 0 }}>
        {/* Header */}
        <div style={{ padding: '16px 14px 12px', borderBottom: `1px solid ${T.borderLight}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 600, color: T.ink, marginBottom: 4, letterSpacing: '-0.02em' }}>Conversations</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>{totalChats} chats · {spaces.length} spaces</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={createFolder}
                title="New Folder"
                style={{
                  width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${T.border}`, background: T.bgWhite,
                  cursor: 'pointer', color: T.textSoft,
                }}
              ><FolderPlus size={15} /></button>
              <button
                onClick={handleNewChat}
                title="New chat"
                style={{ width: 36, height: 32, borderRadius: 9, border: 'none', background: T.green, color: 'white', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 300 }}>
                +
              </button>
            </div>
          </div>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <input
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 10px 9px 30px', borderRadius: 10, border: `1px solid ${T.border}`, fontSize: 12.5, color: T.text, background: T.bg, outline: 'none' }}
              onFocus={e => e.target.style.borderColor = T.green}
              onBlur={e => e.target.style.borderColor = T.border}
            />
            <div style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: T.textMuted, pointerEvents: 'none' }}>⌕</div>
          </div>

          {/* Filter pills */}
          <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
            {['all', 'personal', 'spaces'].map(k => (
              <button key={k} onClick={() => setFilter(k)} style={{
                flex: 1, padding: '6px 0', borderRadius: 8, border: `1px solid ${filter === k ? T.green : T.borderLight}`,
                background: filter === k ? T.greenLight : 'var(--lyra-color-surface)',
                color: filter === k ? T.green : T.textSoft,
                fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', textTransform: 'capitalize',
              }}>{k}</button>
            ))}
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
          {loading ? (
            <div style={{ padding: 16, fontSize: 12, color: T.textMuted }}>Loading…</div>
          ) : (
            <>
              {/* Personal folders */}
              {filteredFolders.map(folder => (
                <div key={folder.id} style={{ marginBottom: 4 }}>
                  <div
                    onClick={() => setFolderOpen(prev => ({ ...prev, [folder.id]: !prev[folder.id] }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', borderRadius: 7, cursor: 'pointer', marginBottom: 2 }}
                    onMouseEnter={e => e.currentTarget.style.background = T.borderLight}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: 10, color: T.textMuted, width: 10 }}>{folderOpen[folder.id] ? '▼' : '▶'}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.textSoft, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{folder.name}</span>
                    <span style={{ fontSize: 10, color: T.textMuted }}>({folder.sessions.length})</span>
                  </div>
                  {folderOpen[folder.id] && folder.sessions.map(s => (
                    <ConvItem
                      key={s.id}
                      icon="💬"
                      title={s.title}
                      sub={s.model && s.model !== 'auto' ? s.model : 'Auto model'}
                      time={timeAgo(s.updatedAt)}
                      active={selected?.id === s.id}
                      onClick={() => setSelected({ id: s.id, title: s.title, type: 'personal', raw: s.raw })}
                      sessionId={s.id}
                      folders={folders}
                      onRenamed={handleRenamed}
                      onMoved={handleMoved}
                      onDeleted={handleDeleted}
                    />
                  ))}
                </div>
              ))}

              {/* Spaces */}
              {filteredSpaces.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ padding: '5px 8px', fontSize: 11, fontWeight: 700, color: T.textSoft, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                    Spaces ({filteredSpaces.length})
                  </div>
                  {filteredSpaces.map(c => (
                    <ConvItem
                      key={c.id}
                      icon="✦"
                      title={c.title}
                      sub={`${c.members} members · ${c.visibility || 'private'}`}
                      time={timeAgo(c.lastActivity)}
                      active={selected?.id === c.id}
                      onClick={() => setSelected({ ...c })}
                      sessionId={c.id}
                      isSpace
                      raw={c.raw}
                      onDeleted={handleSpaceDeleted}
                      pinned={pinnedSpaces.some(id => String(id) === String(c.id))}
                      onTogglePin={togglePin}
                    />
                  ))}
                </div>
              )}

              {/* Empty state */}
              {filteredFolders.length === 0 && filteredSpaces.length === 0 && (
                <div style={{ padding: '32px 16px', textAlign: 'center', color: T.textMuted, fontSize: 12 }}>
                  {search ? `No results for "${search}"` : 'No conversations yet'}
                  {!search && <div style={{ marginTop: 8 }}><button onClick={handleNewChat} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: T.green, color: 'white', cursor: 'pointer', fontSize: 12 }}>Start a chat</button></div>}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Main feed ── */}
      <div className="conv-main-pane" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: 'var(--lyra-color-surface-2)' }}>
        {isMobile && (
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}`, background: T.bgWhite, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <button
              onClick={() => setMobileSidebarOpen(v => !v)}
              style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${T.border}`, background: T.bgWhite, color: T.textMid, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
              title={mobileSidebarOpen ? 'Close conversations list' : 'Open conversations list'}
            >
              {mobileSidebarOpen ? <X size={16} /> : <Menu size={16} />}
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selected?.title || 'Conversations'}
              </div>
            </div>
          </div>
        )}
        {/* Active agents bar */}
        {/* <div style={{ padding: '8px 16px', borderBottom: `1px solid ${T.border}`, background: T.bgWhite, minHeight: 44, display: 'flex', alignItems: 'center' }}>
          <ActiveAgentDock />
        </div> */}

        <div style={{ flex: 1, overflow: 'hidden' }}>
          {selected ? (
            selected.type === 'personal'
              ? <PersonalFeed key={selected.id} sessionId={selected.id} onOpenContext={() => setContextOpen(true)} />
              : <SpaceFeed key={selected.id} spaceId={selected.id} onOpenContext={() => setContextOpen(true)} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.textMuted, padding: 20 }}>
              <div style={{ fontSize: 34, lineHeight: 1.1, fontWeight: 600, color: T.text, letterSpacing: '-0.02em', textAlign: 'center' }}>What can I help you accomplish?</div>
              <div style={{ fontSize: 13, marginTop: 12, color: T.textSoft, textAlign: 'center' }}>Plan a project · Research something · Analyze data · Work on your repository</div>
              <button onClick={handleNewChat} style={{ marginTop: 18, padding: '9px 18px', borderRadius: 11, background: T.green, color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Start a new chat</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Context drawer (progressive disclosure) ── */}
      {!isMobile && contextOpen && (
        <>
          <div
            onClick={() => setContextOpen(false)}
            style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.2)', zIndex: 75 }}
          />
          <div style={{ width: css.ctxWidth, borderLeft: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', background: T.bgWhite, flexShrink: 0, position: 'absolute', top: 0, right: 0, bottom: 0, zIndex: 80, boxShadow: '-10px 0 24px rgba(15,23,42,0.12)' }}>
            <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.textSoft, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Context</div>
              <button onClick={() => setContextOpen(false)} style={{ width: 28, height: 28, borderRadius: 8, border: `1px solid ${T.border}`, background: 'var(--lyra-color-surface)', color: T.textSoft, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label="Close context panel">
                <X size={14} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 12, fontSize: 12, color: T.textSoft }}>
              <ContextPanel conversation={selected} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Space context menu (delete only for owners) ───────────────────────────
function SpaceContextMenu({ spaceId, spaceRaw, onClose, onDeleted, pinned, onTogglePin }) {
  const menuRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const meId = getUserIdFromToken();
  const isOwner = spaceRaw && (
    String(spaceRaw.ownerId) === String(meId) ||
    String(spaceRaw.createdBy) === String(meId) ||
    String(spaceRaw.createdById) === String(meId) ||
    String(spaceRaw.owner) === String(meId) ||
    String(spaceRaw.userId) === String(meId)
  );

  const doDelete = async () => {
    if (!window.confirm('Delete this space? This cannot be undone.')) return;
    try {
      await spacesAPI.delete(spaceId);
      onDeleted(spaceId);
      toast.success('Space deleted');
    } catch (err) {
      console.error('Failed to delete space', err);
      toast.error('Failed to delete space');
    }
    onClose();
  };

  const doTogglePin = async () => {
    try {
      // Toggle client-side pinned state
      if (onTogglePin) onTogglePin(spaceId);
      toast.success(pinned ? 'Unpinned' : 'Pinned');
    } catch (err) {
      console.error('Pin toggle failed', err);
      toast.error('Failed to update pin');
    }
    // keep menu open? close after action
    onClose();
  };

  const menuStyle = {
    position: 'absolute', right: 0, top: '100%', zIndex: 200,
    background: T.bgWhite, border: `1px solid ${T.border}`,
    borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.13)', minWidth: 180, overflow: 'hidden', marginTop: 4,
  };

  return (
    <div ref={menuRef} style={menuStyle}>
      <div style={{ padding: 6 }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: T.textMid, lineHeight: '1.2' }}
          onClick={doTogglePin}
        >
          {pinned ? <Pin size={14} color={T.textMid} /> : <Pin size={14} color={T.textMid} />}<span style={{ display: 'inline-block' }}>{pinned ? 'Unpin' : 'Pin'}</span>
        </div>
        {isOwner ? (
          <div style={{ paddingTop: 6 }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', color: T.red, fontSize: 12, lineHeight: '1.2' }}
              onClick={doDelete}
            >
              <span style={{ fontSize: 13 }}>🗑</span>
              <span>Delete space</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
